import { getApi, postApi } from "../helpers/auth_helpers";
import { getUserToken } from "../helpers/auth_helpers";

const base = () => process.env.REACT_APP_SERVERURL || "";

export async function fetchNextOrgSrNo(): Promise<number | null> {
  const json: any = await getApi(`${base()}/product/next-org-srno`);
  if (json?.success && json?.data?.orgSrNo != null) {
    return Number(json.data.orgSrNo);
  }
  return null;
}

const LEGACY_LOOKUP_MESSAGES = [
  "Tag not in registry",
  "Enter a numeric HKP tag number",
  "Tag registry is disabled",
];

export async function lookupHkpTag(
  tagNo: string,
  productUniqueId?: string
): Promise<{ success: boolean; message?: string; xuid?: string }> {
  const qs = new URLSearchParams({ tagNo: String(tagNo || "").trim() });
  if (productUniqueId) {
    qs.set("productUniqueId", productUniqueId);
  }
  const json: any = await getApi(`${base()}/product/nfc-tag/lookup?${qs.toString()}`);
  if (json?.success && json?.data?.xuid) {
    return { success: true, xuid: json.data.xuid, message: json.message };
  }
  return { success: false, message: json?.message || "Tag lookup failed" };
}

/** Registry hit, legacy (type xuid yourself), or a blocking allotment/bind error. */
export async function resolveHkpTagLookup(
  tagNo: string,
  productUniqueId?: string
): Promise<{
  fromRegistry: boolean;
  legacy: boolean;
  xuid?: string;
  error?: string;
}> {
  const trimmed = String(tagNo || "").trim();
  if (!trimmed) {
    return { fromRegistry: false, legacy: true };
  }
  const result = await lookupHkpTag(trimmed, productUniqueId);
  if (result.success && result.xuid) {
    return { fromRegistry: true, legacy: false, xuid: result.xuid };
  }
  const message = result.message || "Tag lookup failed";
  if (LEGACY_LOOKUP_MESSAGES.includes(message)) {
    return { fromRegistry: false, legacy: true };
  }
  return { fromRegistry: false, legacy: false, error: message };
}

export function parseOrgSrNo(value: any): number | undefined {
  if (value === "" || value == null) {
    return undefined;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

export async function fetchProductFamily(parentId: string): Promise<any[]> {
  if (!parentId) {
    return [];
  }
  const json: any = await getApi(
    `${base()}/product/family?parentId=${encodeURIComponent(parentId)}`
  );
  return json?.success && Array.isArray(json.data) ? json.data : [];
}

export async function linkProductFamily(
  productUniqueId: string,
  targetProductUniqueId: string
): Promise<{ success: boolean; message?: string }> {
  const json: any = await postApi(`${base()}/product/family/link`, {
    productUniqueId,
    targetProductUniqueId,
  });
  return { success: !!json?.success, message: json?.message };
}

export async function delinkProductFamily(
  productUniqueId: string
): Promise<{ success: boolean; message?: string }> {
  const json: any = await postApi(`${base()}/product/family/delink`, {
    productUniqueId,
  });
  return { success: !!json?.success, message: json?.message };
}

export async function listNfcTags(filters: {
  organizationId?: string;
  status?: string;
  fromTagNo?: string;
  toTagNo?: string;
  offset?: number;
  limit?: number;
}): Promise<any> {
  const qs = new URLSearchParams();
  if (filters.organizationId) qs.set("organizationId", filters.organizationId);
  if (filters.status) qs.set("status", filters.status);
  if (filters.fromTagNo) qs.set("fromTagNo", filters.fromTagNo);
  if (filters.toTagNo) qs.set("toTagNo", filters.toTagNo);
  qs.set("offset", String(filters.offset ?? 0));
  qs.set("limit", String(filters.limit ?? 50));
  return getApi(`${base()}/nfc-tags?${qs.toString()}`);
}

export async function allotNfcTags(payload: {
  organizationId: string;
  fromTagNo: number;
  toTagNo: number;
}): Promise<any> {
  return postApi(`${base()}/nfc-tags/allot`, payload);
}

export async function syncIxkioTags(): Promise<any> {
  return postApi(`${base()}/nfc-tags/sync-ixkio`, {});
}

export async function importNfcTagCsv(file: File): Promise<any> {
  const token = getUserToken();
  const body = new FormData();
  body.append("file", file);
  const response = await fetch(`${base()}/nfc-tags/import`, {
    method: "POST",
    headers: { Authorization: "Bearer " + token },
    body,
  });
  return response.json();
}
